/**
 * localStorage utility for persisting pending (not-yet-processed) images
 * between page refreshes on Step 3 (Upload Images).
 *
 * Images are keyed by bookId so each book has its own pending list.
 * Only serializable metadata is stored — no File objects or blob URLs.
 */

const STORAGE_PREFIX = 'pendingImages_';
const EXPECTED_INTERIOR_COUNT_PREFIX = 'expectedInteriorPageCount_';

/**
 * Signed image-API URLs (`…&expires=…&signature=…`) are time-limited bearer
 * tokens — persisting one works for the TTL window, then breaks after reload.
 * Staging books resolve display URLs via the batch refetch instead; only the
 * staging keys are durable. Cloudinary URLs carry no expires/signature params,
 * so legacy entries pass through unchanged.
 */
const isExpiringSignedUrl = (url) =>
  typeof url === 'string' && /[?&]expires=/.test(url) && /[?&]signature=/.test(url);

const persistableUrl = (url) => (url && !isExpiringSignedUrl(url) ? url : null);

/** Persistable form of croppedCloudinaryUrl — staging refs keep keys, drop signed url. */
const persistableCroppedRef = (cropped) => {
  if (!cropped) return null;
  if (typeof cropped === 'string') return persistableUrl(cropped);
  if (typeof cropped !== 'object') return null;
  return { ...cropped, url: persistableUrl(cropped.url) };
};

/** True when a pending entry belongs to the S3 staging path (keys are durable). */
const hasStagingKeys = (entry) =>
  !!entry &&
  [entry.stagingKey, entry.sourceStagingKey, entry.croppedStagingKey, entry.publicId].some(
    (key) => typeof key === 'string' && key.startsWith('staging/'),
  );

/** Cropped preview URL (Cloudinary or S3 staging). */
export const getCroppedCloudinaryDisplayUrl = (img) => {
  if (!img?.croppedCloudinaryUrl) return null;
  if (typeof img.croppedCloudinaryUrl === 'object') {
    return img.croppedCloudinaryUrl.url || null;
  }
  return typeof img.croppedCloudinaryUrl === 'string'
    ? img.croppedCloudinaryUrl
    : null;
};

/** Alias for grid display — prefers cropped preview, then persisted original. */
export const getCroppedPreviewDisplayUrl = (img) =>
  getCroppedCloudinaryDisplayUrl(img) ||
  (img?.croppedStagingKey && img?.url && !img.url.startsWith('blob:') ? img.url : null);

/** Thumbnail src for Choose Photos grid — local blob beats shared signed preview URL. */
export const getGridThumbnailUrl = (img) =>
  img?.blobUrl ||
  getCroppedPreviewDisplayUrl(img) ||
  img?.url ||
  null;

/** Best URL to persist for grid display after reload (prefer cropped preview). */
const getPendingPersistedUrl = (img) =>
  getCroppedPreviewDisplayUrl(img) ||
  img.originalUrl ||
  (img.url && !img.url.startsWith('blob:') ? img.url : null);

/** Restore a pending localStorage row for the Choose Photos grid. */
export const hydratePendingImageForGrid = (pending) => {
  if (!pending) return pending;
  const displayUrl = getCroppedPreviewDisplayUrl(pending) || pending.url;
  return {
    ...pending,
    url: displayUrl,
    originalUrl: pending.originalUrl || pending.url,
  };
};

/**
 * Extract only the serializable, restorable fields from an image object.
 */
const serializeImage = (img, existingEntry = null, gridOrdering = null) => ({
  id: img.id || img._id,
  filename: img.filename || null,
  url: persistableUrl(getPendingPersistedUrl(img)),
  originalUrl: persistableUrl(img.originalUrl),
  publicId: img.publicId || null,
  stagingKey: img.stagingKey || img.publicId || null,
  isCropped: !!img.isCropped,
  cropData: img.cropData || null,
  cropReview: img.cropReview || null,
  croppedCloudinaryUrl: persistableCroppedRef(img.croppedCloudinaryUrl),
  croppedStagingKey: img.croppedStagingKey || null,
  sentToRun: !!img.sentToRun,
  gridOrdering:
    gridOrdering ??
    img.gridOrdering ??
    existingEntry?.gridOrdering ??
    null,
});

/**
 * Save pending images to localStorage for a given book.
 * Only saves images that do NOT have an AIUrl (i.e., not yet processed).
 * Images that came from the DB (have _id and AIUrl) are skipped.
 */
export const savePendingImages = (bookId, images) => {
  if (!bookId || !Array.isArray(images)) return;

  try {
    const existing = loadPendingImages(bookId);
    const existingById = new Map(existing.map((entry) => [entry.id, entry]));

    // Only persist images that haven't been AI-processed yet
    const pendingOnly = images.filter((img) => !img.AIUrl && !img.AIinfo);
    const serialized = pendingOnly
      .map((img) => {
        const key = img.id || img._id;
        const existingEntry = existingById.get(key) || null;
        const gridIndex = images.findIndex(
          (row) => (row.id || row._id) === key,
        );
        const gridOrdering = gridIndex >= 0 ? gridIndex : null;
        const row = serializeImage(img, existingEntry, gridOrdering);
        if (existingEntry?.sentToRun && !img.sentToRun && !img.isCropped) {
          return {
            ...row,
            sentToRun: true,
            publicId: existingEntry.publicId || row.publicId,
            // Older entries may still hold signed URLs from before the
            // keys-only change — never write those back.
            url: persistableUrl(existingEntry.url) || row.url,
            originalUrl: persistableUrl(existingEntry.originalUrl) || row.originalUrl,
          };
        }
        return row;
      })
      // Staging entries survive without a URL — their keys are the durable
      // identity and display URLs come from the batch refetch after reload.
      .filter((img) => img.url || hasStagingKeys(img));
    localStorage.setItem(`${STORAGE_PREFIX}${bookId}`, JSON.stringify(serialized));
  } catch (err) {
    console.error('Failed to save pending images to localStorage:', err);
  }
};

/**
 * Load pending images from localStorage for a given book.
 * Returns an empty array if nothing is stored or on error.
 */
export const loadPendingImages = (bookId) => {
  if (!bookId) return [];

  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${bookId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error('Failed to load pending images from localStorage:', err);
    return [];
  }
};

/** True when localStorage still has pages marked sentToRun (queued/in flight). */
export const hasImagesSentToRunPending = (bookId) =>
  loadPendingImages(bookId).some((p) => p.sentToRun);

/**
 * Persist the full interior grid size for the current /run.
 * Mirrored from the API so Preview keeps the full grid if pending is cleared early.
 *
 * The run initiator (PlaceOrderContent) calls this authoritatively to reset the
 * value at the start of each run. Mid-run signals (per-chunk socket events) pass
 * `{ growOnly: true }` so a per-worker count can never shrink the grid — the
 * whole class of "placeholders vanish at the chunk boundary" bugs.
 */
export const setExpectedInteriorPageCount = (bookId, count, { growOnly = false } = {}) => {
  if (!bookId) return;

  const n = Math.max(0, Number(count) || 0);
  if (n === 0) {
    if (growOnly) return; // never clear from a mid-run signal
    clearExpectedInteriorPageCount(bookId);
    return;
  }

  if (growOnly && loadExpectedInteriorPageCount(bookId) >= n) {
    return; // keep the larger, already-established grid size
  }

  try {
    localStorage.setItem(`${EXPECTED_INTERIOR_COUNT_PREFIX}${bookId}`, String(n));
  } catch (err) {
    console.error('Failed to save expected interior page count:', err);
  }
};

export const loadExpectedInteriorPageCount = (bookId) => {
  if (!bookId) return 0;

  try {
    const raw = localStorage.getItem(`${EXPECTED_INTERIOR_COUNT_PREFIX}${bookId}`);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch (err) {
    console.error('Failed to load expected interior page count:', err);
    return 0;
  }
};

export const clearExpectedInteriorPageCount = (bookId) => {
  if (!bookId) return;

  try {
    localStorage.removeItem(`${EXPECTED_INTERIOR_COUNT_PREFIX}${bookId}`);
  } catch (err) {
    console.error('Failed to clear expected interior page count:', err);
  }
};

/**
 * Resolve how many interior slots Preview should show for the current /run batch.
 * While a job is active, use the largest trustworthy count so a partial DB
 * snapshot (per-chunk totalImageCount, server-driven subset, etc.) cannot
 * shrink the grid mid-run. When idle, prefer the client /run mirror.
 */
export const resolveExpectedInteriorPageCount = (
  bookId,
  { activeBook, lineartProgress } = {},
) => {
  const fromStorage = loadExpectedInteriorPageCount(bookId);
  const fromBook = Number(activeBook?.processingExpectedInteriorCount);
  const fromRedux = Number(lineartProgress?.totalImages);

  const candidates = [fromStorage, fromBook, fromRedux].filter(
    (n) => Number.isFinite(n) && n > 0,
  );

  if (candidates.length === 0) return 0;

  if (isLineartJobActiveForBook(bookId, { activeBook, lineartProgress })) {
    return Math.max(...candidates);
  }

  if (fromStorage > 0) return fromStorage;
  if (Number.isFinite(fromBook) && fromBook > 0) return fromBook;
  if (Number.isFinite(fromRedux) && fromRedux > 0) return fromRedux;

  return 0;
};

/** True while a line-art job is still in flight for this book. */
export const isLineartJobActiveForBook = (
  bookId,
  { activeBook, lineartProgress } = {},
) => {
  if (hasImagesSentToRunPending(bookId)) return true;
  if (lineartProgress?.isProcessing) return true;
  return (
    activeBook?.isProcessing === true &&
    activeBook?.processingWorkflowType === 'lineart' &&
    (activeBook?.processingPercentage ?? 0) < 100
  );
};

/**
 * Resolve totalImages for progress UI without overwriting the /run batch size
 * with partial sketch counts or zero from sparse socket payloads.
 */
export const resolveLineartProgressTotalImages = (
  bookId,
  {
    incoming,
    prevProgress,
    activeBook,
    lineartJobExpectedTotal = 0,
  } = {},
) => {
  const fromIncoming = Number(incoming);
  if (Number.isFinite(fromIncoming) && fromIncoming > 0) return fromIncoming;

  const fromStorage = loadExpectedInteriorPageCount(bookId);
  if (fromStorage > 0) return fromStorage;

  const fromJobRef = Number(lineartJobExpectedTotal);
  if (fromJobRef > 0) return fromJobRef;

  const fromBook = Number(activeBook?.processingExpectedInteriorCount);
  if (Number.isFinite(fromBook) && fromBook > 0) return fromBook;

  const fromPrev = Number(prevProgress?.totalImages);
  if (Number.isFinite(fromPrev) && fromPrev > 0) return fromPrev;

  return 0;
};

/**
 * Remove all pending images for a given book (e.g., after /run completes).
 */
export const clearPendingImages = (bookId) => {
  if (!bookId) return;

  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${bookId}`);
  } catch (err) {
    console.error('Failed to clear pending images from localStorage:', err);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Cover image persistence (Step 2)
// Stored separately so it doesn't interfere with lineart pending images.
// A book has at most 1 cover image, stored as a single object.
// ─────────────────────────────────────────────────────────────────────────────

const COVER_PREFIX = 'pendingCoverImage_';

/**
 * Save the pending cover image for a book.
 * Skips only when a finished AI sketch exists (so re-crops after generation still persist).
 */
export const savePendingCoverImage = (bookId, coverImage) => {
  if (!bookId || !coverImage) return;

  const hasFinishedSketch =
    !!coverImage.AIUrl || !!coverImage.AIinfo?.aws_upload?.s3_url;
  if (hasFinishedSketch) return;

  try {
    const serialized = {
      id: coverImage.id || coverImage._id,
      filename: coverImage.filename || null,
      url: persistableUrl(
        coverImage.croppedCloudinaryUrl?.url ||
          (typeof coverImage.croppedCloudinaryUrl === 'string'
            ? coverImage.croppedCloudinaryUrl
            : null) ||
          coverImage.originalUrl ||
          (coverImage.url && !coverImage.url.startsWith('blob:') ? coverImage.url : null),
      ),
      originalUrl: persistableUrl(coverImage.originalUrl),
      publicId: coverImage.publicId || null,
      stagingKey: coverImage.stagingKey || coverImage.publicId || null,
      isCropped: !!coverImage.isCropped,
      cropData: coverImage.cropData || null,
      cropReview: coverImage.cropReview || null,
      croppedCloudinaryUrl: persistableCroppedRef(coverImage.croppedCloudinaryUrl),
      croppedStagingKey: coverImage.croppedStagingKey || null,
      sentToRun: !!coverImage.sentToRun,
    };

    // Staging covers persist by key alone; legacy covers still need a URL.
    if (!serialized.url && !hasStagingKeys(serialized)) return;

    localStorage.setItem(`${COVER_PREFIX}${bookId}`, JSON.stringify(serialized));
  } catch (err) {
    console.error('Failed to save pending cover image to localStorage:', err);
  }
};

/**
 * Load the pending cover image from localStorage for a given book.
 * Returns null if nothing is stored or on error.
 */
export const loadPendingCoverImage = (bookId) => {
  if (!bookId) return null;

  try {
    const raw = localStorage.getItem(`${COVER_PREFIX}${bookId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    console.error('Failed to load pending cover image from localStorage:', err);
    return null;
  }
};

/** True when a pending cover record and a grid cover row are the same photo. */
export const isSamePendingCover = (pending, cover) =>
  !!pending &&
  !!cover &&
  ((pending.id && (String(pending.id) === String(cover._id || cover.id))) ||
    (!!pending.stagingKey && pending.stagingKey === cover.stagingKey) ||
    (!!pending.croppedStagingKey && pending.croppedStagingKey === cover.croppedStagingKey));

/**
 * The server never stores the crop rectangle, so a cover loaded from the
 * database comes back without the one the auto crop (or the crop tool)
 * measured. Copy it, and the review, from the pending record when that
 * record is the same photo. Rows that already carry one are left alone.
 */
export const carryPendingCoverCropData = (cover, bookId) => {
  if (!cover || cover.cropData) return cover;
  const pending = loadPendingCoverImage(bookId);
  if (!isSamePendingCover(pending, cover) || !pending.cropData) return cover;
  return { ...cover, cropData: pending.cropData, cropReview: pending.cropReview || null };
};

/**
 * Remove the pending cover image entry for a book (e.g., after /run completes).
 */
export const clearPendingCoverImage = (bookId) => {
  if (!bookId) return;

  try {
    localStorage.removeItem(`${COVER_PREFIX}${bookId}`);
  } catch (err) {
    console.error('Failed to clear pending cover image from localStorage:', err);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Lineart pending images (Step 3) — single image removal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Remove a single pending image by its id.
 */
export const removePendingImage = (bookId, imageId) => {
  if (!bookId || !imageId) return;

  try {
    const images = loadPendingImages(bookId);
    const filtered = images.filter(
      (img) => img.id !== imageId && img.id !== String(imageId),
    );
    if (filtered.length > 0) {
      localStorage.setItem(`${STORAGE_PREFIX}${bookId}`, JSON.stringify(filtered));
    } else {
      localStorage.removeItem(`${STORAGE_PREFIX}${bookId}`);
    }
  } catch (err) {
    console.error('Failed to remove pending image from localStorage:', err);
  }
};

/**
 * Mark a single pending image as sent to /run.
 * Optionally updates the publicId and url to match what was actually sent to /run
 * (e.g. after auto-crop re-upload produces a new Cloudinary asset).
 */
export const markImageAsSentToRun = (bookId, imageId, { publicId, url } = {}) => {
  if (!bookId || !imageId) return;

  try {
    const images = loadPendingImages(bookId);
    const idStr = String(imageId);
    const idx = images.findIndex(
      (img) => img.id === imageId || img.id === idStr,
    );
    const persistedUrl = persistableUrl(url);
    const patch = {
      sentToRun: true,
      ...(publicId ? { publicId } : {}),
      ...(persistedUrl ? { url: persistedUrl, originalUrl: persistedUrl } : {}),
    };

    const updated =
      idx >= 0
        ? images.map((img, i) => (i === idx ? { ...img, ...patch } : img))
        : [...images, { id: idStr, ...patch }];

    localStorage.setItem(`${STORAGE_PREFIX}${bookId}`, JSON.stringify(updated));
  } catch (err) {
    console.error('Failed to mark image as sent to run in localStorage:', err);
  }
};

/** Clear stale sentToRun flags when no RunPod job is active for this book. */
export const reconcileStaleSentToRunQueue = (bookId, { isJobActive = false } = {}) => {
  if (!bookId || isJobActive) return false;

  try {
    const pending = loadPendingImages(bookId);
    if (!pending.some((p) => p.sentToRun)) return false;

    const updated = pending.map((p) =>
      p.sentToRun ? { ...p, sentToRun: false } : p,
    );
    localStorage.setItem(`${STORAGE_PREFIX}${bookId}`, JSON.stringify(updated));
    return true;
  } catch (err) {
    console.error('Failed to reconcile stale sentToRun queue:', err);
    return false;
  }
};

/** Clear queued flag when user deletes or re-crops a page. */
export const clearSentToRunForImage = (bookId, imageId) => {
  if (!bookId || !imageId) return;

  try {
    const images = loadPendingImages(bookId);
    const idStr = String(imageId);
    const updated = images.map((img) =>
      img.id === imageId || img.id === idStr
        ? { ...img, sentToRun: false }
        : img,
    );
    localStorage.setItem(`${STORAGE_PREFIX}${bookId}`, JSON.stringify(updated));
  } catch (err) {
    console.error('Failed to clear sentToRun in localStorage:', err);
  }
};

/**
 * Mark the pending cover image as sent to /run.
 */
/** Clear queued flag when the user re-crops an already-generated cover. */
export const clearCoverSentToRun = (bookId) => {
  if (!bookId) return;

  try {
    const cover = loadPendingCoverImage(bookId);
    if (!cover) return;
    cover.sentToRun = false;
    localStorage.setItem(`${COVER_PREFIX}${bookId}`, JSON.stringify(cover));
  } catch (err) {
    console.error('Failed to clear cover sentToRun in localStorage:', err);
  }
};

export const markCoverAsSentToRun = (bookId) => {
  if (!bookId) return;

  try {
    const cover = loadPendingCoverImage(bookId);
    if (cover) {
      cover.sentToRun = true;
      localStorage.setItem(`${COVER_PREFIX}${bookId}`, JSON.stringify(cover));
    }
  } catch (err) {
    console.error('Failed to mark cover as sent to run in localStorage:', err);
  }
};
